import {
  aantalBezoeken,
  STATUS_TEKST,
  afkoppelStatus,
  contactmomentenOpDatum,
  totaleBergingM3,
  type Registratie,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { adresRegel, datumNL, vandaagISO } from '@/utils/format';
import { downloadBlob } from '@/utils/download';
import { maakXlsx, type CelWaarde, type Kolom } from '@/utils/xlsx';

/** Kolomdefinitie van de export; volgorde bepaalt de kolomvolgorde in Excel. */
const KOLOMMEN: Kolom[] = [
  { kop: 'Adres', breedte: 28 },
  { kop: 'Postcode', breedte: 11 },
  { kop: 'Plaats', breedte: 18 },
  { kop: 'Koop / huur', breedte: 12 },
  { kop: 'BAG-id', breedte: 20 },
  { kop: 'Latitude', breedte: 12 },
  { kop: 'Longitude', breedte: 12 },
  { kop: 'Datum laatste contact', breedte: 16 },
  { kop: 'Aantal contactmomenten', breedte: 18 },
  { kop: 'Waarvan bezoeken', breedte: 15 },
  { kop: 'Verloop contact', breedte: 50 },
  { kop: 'Afkoppelcoach', breedte: 20 },
  { kop: 'Uitkomst bezoek', breedte: 24 },
  { kop: 'Toelichting bezoek', breedte: 40 },
  { kop: 'Naam bewoner', breedte: 22 },
  { kop: 'Telefoon', breedte: 14 },
  { kop: 'E-mail', breedte: 24 },
  { kop: 'Toestemming AVG', breedte: 16 },
  { kop: 'Toelichting bewoner', breedte: 30 },
  { kop: 'Voortuin aanwezig', breedte: 16 },
  { kop: 'Advies voortuin', breedte: 40 },
  { kop: 'Berging voortuin (m³)', breedte: 18 },
  { kop: 'Toelichting voortuin', breedte: 30 },
  { kop: 'Achtertuin aanwezig', breedte: 17 },
  { kop: 'Advies achtertuin', breedte: 40 },
  { kop: 'Berging achtertuin (m³)', breedte: 19 },
  { kop: 'Toelichting achtertuin', breedte: 30 },
  { kop: 'Totale berging (m³)', breedte: 17 },
  { kop: 'Afkoppelen pand', breedte: 22 },
  { kop: 'Beslist over het pand', breedte: 20 },
  { kop: 'Besluit eigenaar over het pand', breedte: 26 },
  { kop: 'Extra maatregelen op het perceel', breedte: 28 },
  { kop: 'Status subsidieaanvraag', breedte: 22 },
  { kop: 'Status realisatie', breedte: 18 },
  { kop: 'Datum vervolgafspraak', breedte: 18 },
  { kop: 'Algemene toelichting', breedte: 40 },
  { kop: 'Foto’s voorkant', breedte: 14 },
  { kop: 'Foto’s achterkant', breedte: 15 },
  { kop: 'Foto’s overig', breedte: 13 },
  { kop: 'Laatst gewijzigd', breedte: 18 },
];

function aantalFotos(registratie: Registratie, soort: string): number {
  return registratie.fotos.filter((foto) => foto.soort === soort).length;
}

function naarRij(registratie: Registratie): CelWaarde[] {
  const { voortuin, achtertuin, bewoner, adres } = registratie;

  return [
    adresRegel(adres),
    adres.postcode,
    adres.plaats,
    registratie.eigendom,
    registratie.bagId,
    registratie.latitude,
    registratie.longitude,
    datumNL(registratie.datumBezoek),
    registratie.contactmomenten.length,
    aantalBezoeken(registratie),
    // Het hele verloop in één cel: datum, soort, uitkomst en wat er is
    // besproken. Zo is de voortgang ook in Excel te volgen zonder de app
    // erbij — inclusief de notities, want dit is de eigen export van de
    // coaches.
    contactmomentenOpDatum(registratie.contactmomenten)
      .map((moment) =>
        [datumNL(moment.datum), moment.soort, moment.uitkomst, moment.toelichting]
          .filter(Boolean)
          .join(' — ')
      )
      .join(' | '),
    registratie.coach,
    registratie.uitkomstBezoek,
    registratie.uitkomstToelichting,
    bewoner.naam,
    bewoner.telefoon,
    bewoner.email,
    bewoner.toestemmingPersoonsgegevens,
    bewoner.toelichting,
    voortuin.aanwezig,
    voortuin.advies.join(', '),
    voortuin.bergingM3,
    voortuin.toelichting,
    achtertuin.aanwezig,
    achtertuin.advies.join(', '),
    achtertuin.bergingM3,
    achtertuin.toelichting,
    totaleBergingM3(registratie) || null,
    STATUS_TEKST.afkoppelen[afkoppelStatus(registratie, isHuurwoning(registratie.adres))],
    isHuurwoning(registratie.adres) ? 'Corporatie' : 'Eigenaar',
    isHuurwoning(registratie.adres) ? '' : registratie.afkoppelbesluit,
    registratie.maatregelenbesluit,
    registratie.subsidieStatus,
    registratie.realisatieStatus,
    datumNL(registratie.datumVervolg),
    registratie.algemeneToelichting,
    aantalFotos(registratie, 'Voorkant') || null,
    aantalFotos(registratie, 'Achterkant') || null,
    aantalFotos(registratie, 'Overig') || null,
    datumNL(registratie.gewijzigdOp),
  ];
}

/** Bouwt en downloadt een .xlsx met alle meegegeven registraties. */
export function exporteerNaarExcel(registraties: Registratie[]): void {
  const blob = maakXlsx(KOLOMMEN, registraties.map(naarRij), 'Registraties');
  downloadBlob(blob, `Afkoppelcoach_registraties_${vandaagISO()}.xlsx`);
}

/** CSV-variant (puntkomma + BOM) voor systemen die geen xlsx accepteren. */
export function exporteerNaarCsv(registraties: Registratie[]): void {
  const escape = (waarde: CelWaarde): string => {
    if (waarde === null || waarde === undefined) return '';
    const tekst = typeof waarde === 'boolean' ? (waarde ? 'Ja' : 'Nee') : String(waarde);
    return `"${tekst.replace(/"/g, '""')}"`;
  };

  const regels = [
    KOLOMMEN.map((kolom) => escape(kolom.kop)).join(';'),
    ...registraties.map((registratie) => naarRij(registratie).map(escape).join(';')),
  ];

  // BOM zodat Excel de UTF-8 tekens goed leest.
  const blob = new Blob([`﻿${regels.join('\r\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  downloadBlob(blob, `Afkoppelcoach_registraties_${vandaagISO()}.csv`);
}
